import type { CompressFallbackReason } from './util/compressFile'
import type { UploadStep } from './uploadFile'

/**
 * Structured context for a failed file upload, built once and reported through
 * whatever channel a given app has (PostHog `track` in painter-view, Sentry in
 * apps/app). The iPad upload failures in #21104 were invisible because the only
 * capture was a `Sentry.captureException` in a dialog rendered by painter-view,
 * which has no Sentry — this shape makes the failure loud and self-explanatory
 * wherever it's reported.
 */
export interface UploadFailureContext {
  fileName?: string
  fileType?: string
  fileSize?: number
  /** Which step failed (stamped by `uploadFile`), or 'unknown' (e.g. the 90s guard timeout). */
  uploadStep: UploadStep | 'unknown'
  /** Set if image compression fell back to the original before this failure (iOS suspect). */
  compressFallback?: CompressFallbackReason
  message: string
  userAgent?: string
  isIOS?: boolean
  /** Bytes uploaded before an S3 PUT failed (stamped by `putFileViaXhr`); lets us tell
   *  a request that stalled after fully reaching S3 from one that never left the device. */
  bytesSent?: number
  bytesTotal?: number
  /** XHR readyState at the moment the S3 PUT failed. */
  readyState?: number
}

/**
 * iOS/iPadOS detection. Covers Chrome on iOS (CriOS, which is still WebKit) and
 * iPadOS Safari, which masquerades as "Macintosh" but exposes touch — the exact
 * environment (#21104) where uploads fail.
 */
function detectIOS(userAgent?: string): boolean | undefined {
  if (!userAgent) return undefined
  if (/iPad|iPhone|iPod/.test(userAgent)) return true
  // iPadOS Safari in desktop mode reports as Macintosh but is touch-capable.
  return /Macintosh/.test(userAgent) && typeof document !== 'undefined' && 'ontouchend' in document
}

export function getUploadFailureContext(error: unknown, file?: File): UploadFailureContext {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined
  // Only read fields off the error when it's actually an object — a thrown
  // number/string/null falls through to String(error) for the message.
  const err =
    error && typeof error === 'object'
      ? (error as {
          uploadStep?: UploadStep
          compressFallback?: CompressFallbackReason
          message?: string
          bytesSent?: number
          bytesTotal?: number
          readyState?: number
        })
      : undefined

  return {
    fileName: file?.name,
    fileType: file?.type,
    fileSize: file?.size,
    uploadStep: err?.uploadStep ?? 'unknown',
    compressFallback: err?.compressFallback,
    message: err?.message ?? String(error),
    userAgent,
    isIOS: detectIOS(userAgent),
    bytesSent: err?.bytesSent,
    bytesTotal: err?.bytesTotal,
    readyState: err?.readyState
  }
}
